import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { Honeybadger } from '@honeybadger-io/react';
import { useQueryClient } from '@tanstack/react-query';

import { requestApi } from 'api/Api';

import { iaRa, optionify } from 'lib/helpers/utility';
import { useFacilityProvider } from 'lib/hooks/queries/practice/useFacilityProvider';
import { useStates } from 'lib/hooks/queries/states/useStates';

import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Buttons/Button';
import Allowed from 'components/shared/Permissions/Allowed';

import NewFacilityProvider from './NewFacilityProvider';
import Row from './Row';
import { facilityProviderData } from './lib/facilityProviderData';
import { useFacilityProviderForm } from './lib/useFacilityProviderForm';

export default function FacilityProvider() {
  const navigate = useNavigate();
  const { practice } = useOutletContext();
  const queryClient = useQueryClient();

  const { data: statesData } = useStates();
  const states = optionify(statesData?.states);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    facilityProviderData({
      practiceId: practice?.id,
      navigate
    });
  }, [practice?.id]);
  const { data, isLoading, isFetching } = useFacilityProvider({
    params: { practiceId: practice?.id },
    dependencies: [practice?.id]
  });

  const facilityInfo = !isLoading && !isFetching && data ? data.facilityInfo : undefined;

  const onSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);

    const { facility_npi, facility_id } = values;
    if (!facility_npi && !facility_id) {
      showAlert({
        title: 'Please provide either Facility NPI or Facility Atypical ID.',
        color: 'danger'
      });
      setSubmitting(false);
      return;
    }

    try {
      const res = await requestApi({
        url: '/api/admin/practice/facility/upsert',
        params: { newFacilityProvider: values },
        navigate
      });

      if (res?.success) {
        showAlert({ title: 'Success', color: 'primary' });
        queryClient.invalidateQueries(['facility-provider']);
        setShowModal(false);
        setModalType(null);
      } else {
        showAlert({ title: 'There was an error.', color: 'danger' });
        Honeybadger.notify('Facility upsert failed');
      }
    } catch (error) {
      Honeybadger.notify(error);
      showAlert({ title: 'There was an error.', color: 'danger' });
    }

    setSubmitting(false);
  };

  const formik = useFacilityProviderForm({
    practice,
    onSubmit,
    validate: showModal
  });

  const openModal = (type) => {
    setShowModal(true);
    setModalType(type);
  };

  const selectFacility = (biller) => {
    Object.entries(biller).forEach(([key, val]) => {
      if (Object.prototype.hasOwnProperty.call(formik.values, key)) {
        formik.setFieldValue(key, val);
      }
    });
    openModal('update');
  };

  const closeModal = () => {
    formik.resetForm();
    formik.setValues({
      ...formik.values,
      id: null,
      practice_id: practice.id,
      facility_name: practice.name,
      facility_npi: '',
      facility_id: '',
      facility_addr_1: practice?.practiceAddress?.address_ln_1 || '',
      facility_addr_2: practice?.practiceAddress?.address_ln_2 || '',
      facility_city: practice?.practiceAddress?.city || '',
      facility_state: practice?.practiceAddress?.state || '',
      facility_zip: (practice?.practiceAddress?.zip || '').replace(/-/g, ''),
      practitioner_ids: []
    });
    setShowModal(false);
  };

  const handleDeletion = async (id) => {
    setIsDeleting(true);
    try {
      const res = await requestApi({
        url: '/api/admin/practice/facility/delete',
        params: { id },
        navigate
      });

      if (res?.success) {
        showAlert({ title: 'Deleted successfully', color: 'primary' });
        queryClient.invalidateQueries(['facility-provider']);
        setShowModal(false);
        setModalType(null);
      } else {
        showAlert({ title: 'There was an error deleting.', color: 'danger' });
        Honeybadger.notify('Facility delete failed');
      }
    } catch (error) {
      Honeybadger.notify(error);
      showAlert({ title: 'There was an error.', color: 'danger' });
    }
    setIsDeleting(false);
  };

  return (
    <Allowed requiredPermissions="practice.update">
      <div className="flex h-fit min-h-[30vh] flex-col p-[1rem]">
        <div className="flex w-full items-center justify-between">
          <h3 className="w-max text-lg font-500 capitalize leading-7 text-primary-800 md:text-base">
            Facility Providers
          </h3>
          <Button
            icon="add-square-mono"
            size="small"
            transparent
            iconColor="white"
            onClick={() => openModal('create')}
            text="New Facility Provider"
            className="w-fit"
          />
        </div>
        <div className="!mt-6 grid grid-cols-2 gap-4 overflow-auto !pr-4">
          {iaRa(facilityInfo)?.map((row) => (
            <Row
              key={row.id}
              row={row}
              members={practice?.members}
              onClick={() => selectFacility(row)}
              states={states}
            />
          ))}
        </div>

        {showModal && (
          <NewFacilityProvider
            formik={formik}
            isLoading={formik.isSubmitting}
            open={showModal}
            type={modalType}
            handleClose={closeModal}
            handleSubmit={formik.submitForm}
            states={states}
            loadedMembers={iaRa(practice?.members).map((member) => ({
              label: `${member.f_name} ${member.l_name}`,
              value: member.id
            }))}
            handleDeletion={handleDeletion}
            isDeleting={isDeleting}
          />
        )}
      </div>
    </Allowed>
  );
}
